import { Stack } from '@mui/joy';
import { upperFirst } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { ICliJob } from 'cli/types';
import { Steps } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { useCliJobHandler } from 'core/hooks';
import { StepsProvider, useNotificationContext } from 'core/states';
import { IStep } from 'core/types';
import { IPartFinderReel } from 'partFinder/types';
import { SeeQuoteButton } from 'quotes/components/AddBomPartsToQuote';
import { QuoteEntityType, QuoteType } from 'quotes/enums';
import { useQuoteActions, useQuotePartActions } from 'quotes/hooks';
import { mapUpsertQuotePartLine } from 'quotes/mappers';
import { IBaseQuote, ICustomerQuote, IQuotePartLineForm } from 'quotes/types';
import { Color, Size } from 'theme/enums';

import { getReelNotes, mapAddReelsToQuoteForm } from './mappers';
import { QuotePicker } from './QuotePicker';
import { ReelLineDetails } from './ReelLineDetails';
import { ReelLinesForm } from './ReelLinesForm';
import { IAddReelsToQuoteForm } from './types';

interface IProps {
  entityType: QuoteEntityType;
  reels: IPartFinderReel[];
  onClose: () => void;
  onSubmitSuccess?: () => void;
}

const ADD_TO_QUOTE_STEP = 'addToQuote';

const drawerSlotAndSlotProps = { slotProps: { content: { sx: { width: '720px' } } } };

// The Add To... drawer of the Part Finder™ results (Figma 2634:765731): pick the customer quote
// or order, or start a new one, then say how much of each picked reel goes on it. A single reel
// added to an existing quote gets the full Add Line form; several reels, or a new quote, get one
// row each.
export const AddToQuoteOrder = ({
  entityType: initialEntityType,
  reels,
  onClose,
  onSubmitSuccess,
}: IProps) => {
  const [entityType, setEntityType] = useState(initialEntityType);
  const [selectedQuote, setSelectedQuote] = useState<ICustomerQuote | null>(null);
  const [isCreatingQuote, setIsCreatingQuote] = useState(false);
  const [linesCount, setLinesCount] = useState(reels?.length ?? 0);
  // Add, Cancel and close wait while the line form validates or prices, as in Add Line.
  const [isLineValidating, setIsLineValidating] = useState(false);

  const linesFormRef = useRef(null);
  const lineDetailsRef = useRef(null);
  const hasAddedPartsRef = useRef(false);

  const { t } = useTranslation();

  const { user } = useAuthUser();
  const { clearDrawerNotification, clearFormNotification, showSuccessNotification } =
    useNotificationContext();
  const { handleCliJob } = useCliJobHandler();
  const { addFinderPartsToCustomerQuote } = useQuoteActions({ entityType });
  const { upsertQuotePart } = useQuotePartActions({
    notificationPosition: NotificationPosition.DRAWER,
  });

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ user });

  const hasLineDetails = !isCreatingQuote && !!selectedQuote?.id && 1 === reels?.length;
  const hasLinesForm = isCreatingQuote || (!!selectedQuote?.id && reels?.length > 1);

  const showPartsAddedNotification = useCallback(
    (quoteId: number, count: number) =>
      showSuccessNotification(
        {
          message: t('messagePartsAddingSuccess', { count }),
          buttons: [
            <SeeQuoteButton key="seeQuoteButton" entityType={entityType} quoteId={quoteId} />,
          ],
          disableAutoClose: true,
        },
        NotificationPosition.TOP
      ),
    [entityType, showSuccessNotification, t]
  );

  const onSubmitLines = useCallback(
    async (closeDrawer: () => void) => {
      let data: IAddReelsToQuoteForm = null;

      try {
        data = await linesFormRef.current.triggerFormSubmit();
      } catch {
        return false;
      }

      const count = data?.lines?.length ?? 0;

      handleCliJob(
        addFinderPartsToCustomerQuote,
        mapAddReelsToQuoteForm({
          reels,
          data,
          targetQuote: isCreatingQuote ? null : selectedQuote,
          entityType,
          isAlternateVersionEnabled,
        }),
        {
          cliJobInProgressMessage: t('messagePartsAddingInProgress', { count }),
          onCliJobFinished: (cliJob: ICliJob) => {
            // Read in both shapes the add-from-BoM flow reads a created quote in.
            const cliResult = cliJob?.result as { quote: IBaseQuote } | { id: number }[];
            const quoteId = isCreatingQuote
              ? (cliResult as { quote: IBaseQuote })?.quote?.id ||
                (cliResult as { id: number }[])?.[0]?.id
              : selectedQuote?.id;

            hasAddedPartsRef.current = true;

            showPartsAddedNotification(quoteId, count);
            closeDrawer();
          },
          notificationPosition: NotificationPosition.DRAWER,
        }
      );

      return false;
    },
    [
      addFinderPartsToCustomerQuote,
      entityType,
      handleCliJob,
      isAlternateVersionEnabled,
      isCreatingQuote,
      reels,
      selectedQuote,
      showPartsAddedNotification,
      t,
    ]
  );

  // Saved the way the Add Line drawer saves a line, with the picked reel added to its notes.
  const onSubmitLineDetails = useCallback(async () => {
    let data: IQuotePartLineForm = null;

    try {
      data = await lineDetailsRef.current.triggerFormSubmit();
    } catch {
      return false;
    }

    const { quotePart } = mapUpsertQuotePartLine(
      selectedQuote.id,
      QuoteType.CUSTOMER,
      { ...data, notes: [...(data?.notes ?? []), ...getReelNotes(reels?.[0])] },
      null,
      null,
      null,
      {
        isAlternateVersionEnabled: AlternateVersionHelper.isAlternateVersionEnabled({
          quote: selectedQuote,
        }),
        isTaxonomyStepEnabled: false,
        isTaxonomyRequired: false,
        hasExternalTaxonomy: false,
      }
    );

    let upsertResult = null;

    try {
      // No taxonomy step here, so the line skips the check, as every multi-line add does.
      upsertResult = await upsertQuotePart({
        quotePart: { ...quotePart, skipTaxonomyRequiredCheck: 1 },
      });
    } catch (error) {
      // Request errors are notified by useHandleErrors; keep the drawer open.
      console.error(error);

      return false;
    }

    if (!upsertResult?.success) {
      return false;
    }

    hasAddedPartsRef.current = true;

    showPartsAddedNotification(selectedQuote.id, 1);

    return true;
  }, [reels, selectedQuote, showPartsAddedNotification, upsertQuotePart]);

  // The app shows one notification at a time, so only the drawer's own are cleared: the
  // "parts added" message with its See Quote button must outlive the drawer.
  const clearDrawerNotifications = useCallback(() => {
    clearDrawerNotification();
    clearFormNotification();
  }, [clearDrawerNotification, clearFormNotification]);

  // The grid's selection is cleared only once the drawer has closed: the drawer belongs to the
  // selection bar, which goes away with the selection.
  const handleOnClose = useCallback(() => {
    clearDrawerNotifications();
    onClose();

    if (hasAddedPartsRef.current) {
      onSubmitSuccess?.();
    }
  }, [clearDrawerNotifications, onClose, onSubmitSuccess]);

  const onChangeEntityType = useCallback(
    (value: QuoteEntityType) => {
      clearDrawerNotifications();
      setEntityType(value);
      setSelectedQuote(null);
    },
    [clearDrawerNotifications]
  );

  const onSelectQuote = useCallback(
    (quote: ICustomerQuote | null) => {
      clearDrawerNotifications();
      setSelectedQuote(quote);
      setIsCreatingQuote(false);
    },
    [clearDrawerNotifications]
  );

  const onCreateQuote = useCallback(() => {
    clearDrawerNotifications();
    setSelectedQuote(null);
    setIsCreatingQuote(true);
  }, [clearDrawerNotifications]);

  const steps: IStep[] = useMemo(
    () => [
      {
        type: ADD_TO_QUOTE_STEP,
        title: t('titleAddToQuote', { quoteType: upperFirst(QuoteType.CUSTOMER), entityType }),
        content: (
          <Stack sx={{ gap: 2 }}>
            <QuotePicker
              entityType={entityType}
              selectedQuote={selectedQuote}
              isCreatingQuote={isCreatingQuote}
              onChangeEntityType={onChangeEntityType}
              onSelectQuote={onSelectQuote}
              onCreateQuote={onCreateQuote}
            />

            {hasLineDetails && (
              <ReelLineDetails
                key={selectedQuote.id}
                ref={lineDetailsRef}
                reel={reels[0]}
                quoteId={selectedQuote.id}
                entityType={entityType}
                onClose={handleOnClose}
                onSubmit={onSubmitLineDetails}
                onValidatingChange={setIsLineValidating}
              />
            )}

            {hasLinesForm && (
              <ReelLinesForm
                key={`${entityType}${isCreatingQuote ? 'New' : selectedQuote?.id}`}
                ref={linesFormRef}
                reels={reels}
                entityType={entityType}
                isCreatingQuote={isCreatingQuote}
                onLinesCountChange={setLinesCount}
              />
            )}
          </Stack>
        ),
        nextButton: {
          text: t('buttonLabelAddToCustomerQuote', { entityType }),
          options: {
            onClick: hasLineDetails ? onSubmitLineDetails : onSubmitLines,
            disabled: isLineValidating || (!hasLineDetails && !(hasLinesForm && linesCount > 0)),
          },
        },
        cancelButton: { options: { disabled: isLineValidating } },
      },
    ],
    [
      entityType,
      handleOnClose,
      hasLineDetails,
      hasLinesForm,
      isCreatingQuote,
      isLineValidating,
      linesCount,
      onChangeEntityType,
      onCreateQuote,
      onSelectQuote,
      onSubmitLineDetails,
      onSubmitLines,
      reels,
      selectedQuote,
      t,
    ]
  );

  return (
    <StepsProvider steps={steps} onClose={handleOnClose}>
      <Steps
        title={t('titleAddToQuote', { quoteType: upperFirst(QuoteType.CUSTOMER), entityType })}
        isOpen
        color={Color.NEUTRAL}
        size={Size.MD}
        fullWidth={false}
        drawerSlotAndSlotProps={drawerSlotAndSlotProps}
        disableClose={isLineValidating}
      />
    </StepsProvider>
  );
};
